/**
 * Grand Quack Auto — camera system.
 *
 * Third-person lerp chase camera. The offset (0, 6, -10) rotates with the
 * duck's Y rotation so the camera always orbits behind the player. Position
 * smoothing uses damp() (Freya Holmer-style exponential) for frame-rate-
 * independent feel. FOV widens from fovMin→fovMax based on movement speed
 * for a cinematic driving feel (per research §3).
 *
 * Collision avoidance (raycast snap) deferred to Day 8 when buildings exist.
 *
 * See research.md §3 "Third-person camera".
 */

import * as THREE from 'three';
import { CAMERA, DUCK } from '../config';
import type { System, World } from '../types';
import { damp, dampAngle, remap } from '../utils/mathUtils';

// Reusable vectors — avoid per-frame allocation.
const _desired = new THREE.Vector3();
const _lookTarget = new THREE.Vector3();
const _rayDir = new THREE.Vector3();
const _raycaster = new THREE.Raycaster();

// Smoothed camera yaw — lags behind duck.rotation.y so the player can
// see the duck actually moving/turning (GTA / Goat Simulator feel).
// Without this, the camera orbits instantly with the duck, making
// left/right input look like spinning in place.
let smoothedYaw = 0;

export const cameraSystem: System = {
  name: 'camera',
  render(dt: number, _alpha: number, world: World) {
    const cam = world.camera;

    // Follow target: car when driving, duck when on foot.
    const target = world.playerMode === 'driving' && world.activeVehicle
      ? world.activeVehicle.group
      : world.duck;

    // Smoothly track target's yaw — lagged for GTA / Goat Simulator feel.
    smoothedYaw = dampAngle(smoothedYaw, target.rotation.y, CAMERA.yawSmoothing, dt);
    const cosT = Math.cos(smoothedYaw);
    const sinT = Math.sin(smoothedYaw);

    _desired.set(
      target.position.x + CAMERA.offset.x * cosT + CAMERA.offset.z * sinT,
      target.position.y + CAMERA.offset.y,
      target.position.z + CAMERA.offset.x * (-sinT) + CAMERA.offset.z * cosT,
    );

    cam.position.x = damp(cam.position.x, _desired.x, CAMERA.followLerp, dt);
    cam.position.y = damp(cam.position.y, _desired.y, CAMERA.followLerp, dt);
    cam.position.z = damp(cam.position.z, _desired.z, CAMERA.followLerp, dt);

    // ── Camera collision avoidance ────────────────────────────────────
    // Raycast from look target to camera. If the ray hits geometry,
    // snap camera forward to prevent clipping through buildings.
    _lookTarget.set(
      target.position.x,
      target.position.y + CAMERA.lookHeadOffset,
      target.position.z,
    );
    // Raycast from look target toward camera — only check the buildings mesh.
    const buildingMesh = world.scene.getObjectByName('buildings');
    if (buildingMesh) {
      _rayDir.copy(cam.position).sub(_lookTarget).normalize();
      const maxDist = cam.position.distanceTo(_lookTarget);
      _raycaster.set(_lookTarget, _rayDir);
      _raycaster.far = maxDist;
      _raycaster.near = 0;
      const hits = _raycaster.intersectObject(buildingMesh, false);
      if (hits.length > 0 && hits[0]!.distance > 2) {
        // Snap camera forward, but keep at least 2 units from target.
        const safeDist = Math.max(2, hits[0]!.distance - 0.5);
        cam.position.copy(_lookTarget).addScaledVector(_rayDir, safeDist);
      }
    }

    cam.lookAt(_lookTarget);

    // FOV: use chassis velocity when driving, input speed when on foot.
    let speed: number;
    if (world.playerMode === 'driving' && world.activeVehicle) {
      const v = world.activeVehicle.chassisBody.velocity;
      speed = Math.sqrt(v.x * v.x + v.y * v.y + v.z * v.z);
    } else {
      speed = Math.hypot(world.input.move.x, world.input.move.y) * DUCK.walkSpeed;
    }
    const maxSpeed = world.playerMode === 'driving' ? 30 : DUCK.runSpeed;
    const targetFov = remap(speed, 0, maxSpeed, CAMERA.fovMin, CAMERA.fovMax);
    cam.fov = damp(cam.fov, targetFov, CAMERA.followLerp, dt);
    cam.updateProjectionMatrix();
  },
};
